import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, Shield, Clock, Zap, DollarSign } from 'lucide-react';

// Responsive Accordion/Grid Component for Strategic Benefits
const WhyChooseRaven = () => {
  const [openPanel, setOpenPanel] = useState(null);

  const benefits = [
    {
      id: 1,
      icon: <Shield className="w-8 h-8 text-emerald-700" />,
      title: 'Compliance Certainty & Capital Protection',
      description: 'Our deep knowledge of Alaska regulations ensures your permits are approved the first time, protecting your capital investment and avoiding costly resubmissions or project delays.'
    },
    {
      id: 2,
      icon: <Clock className="w-8 h-8 text-emerald-700" />,
      title: 'Projected Schedule Reliability',
      description: 'With over 13 years of Alaska permitting experience, we deliver predictable timelines that keep your projects on track and revenue streams flowing without regulatory surprises.'
    },
    {
      id: 3,
      icon: <Zap className="w-8 h-8 text-emerald-700" />,
      title: 'Maximized Operational Uptime',
      description: 'Our right-sized monitoring and compliance strategies minimize operational disruptions, reduce maintenance burdens, and keep your facility running at peak efficiency.'
    },
    {
      id: 4,
      icon: <DollarSign className="w-8 h-8 text-emerald-700" />,
      title: 'Capital-Efficient Design',
      description: 'Avoid over-engineered solutions with our practical approach that meets compliance requirements while saving 30-40% on capital costs compared to traditional consulting methods.'
    }
  ];

  const togglePanel = (id) => {
    setOpenPanel(openPanel === id ? null : id);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-emerald-50 to-cyan-50" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Why Choose Raven Environmental?
          </h2>

          {/* Desktop View: Always Open 2-Column Grid (>1024px) */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-6">
            {benefits.map((benefit) => (
              <div
                key={benefit.id}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                      {benefit.icon}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile/Tablet View: Accordion (<=1024px) */}
          <div className="lg:hidden space-y-4">
            {benefits.map((benefit) => (
              <div
                key={benefit.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                {/* Accordion Header */}
                <button
                  onClick={() => togglePanel(benefit.id)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      {benefit.icon}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {benefit.title}
                    </h3>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 flex-shrink-0 ml-4 ${
                      openPanel === benefit.id ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Accordion Content */}
                <div
                  className={`transition-all duration-300 ease-in-out ${
                    openPanel === benefit.id
                      ? 'max-h-96 opacity-100'
                      : 'max-h-0 opacity-0 overflow-hidden'
                  }`}
                >
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const industries = [
    { name: 'Power Generation', link: '/industries' },
    { name: 'Oil & Gas', link: '/sectors/oil-and-gas' },
    { name: 'Military Installations', link: '/sectors/military-installations' },
    { name: 'Seafood Processing', link: '/sectors/seafood-processing' },
    { name: 'Mining & Construction', link: '/sectors/mining-construction' },
    { name: 'Soil Remediation', link: '/sectors/soil-remediation' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section
        className="relative text-white py-20 md:py-32 overflow-hidden"
        data-testid="hero-section"
      >
        {/* Responsive Background Image */}
        <picture className="absolute inset-0 w-full h-full">
          {/* Desktop Image (>1024px) */}
          <source
            media="(min-width: 1025px)"
            srcSet="/images/raven_juneau_1920x1080.webp"
          />
          {/* Tablet Image (768px-1024px) */}
          <source
            media="(min-width: 768px) and (max-width: 1024px)"
            srcSet="/images/raven_juneau_1536x864.webp"
          />
          {/* Mobile Image (<768px) - Vertical */}
          <source
            media="(max-width: 767px)"
            srcSet="/images/raven_juneau_1080x1920.webp"
          />
          {/* Fallback */}
          <img
            src="/images/raven_juneau_1920x1080.webp"
            alt="Raven in misty Juneau forest"
            className="w-full h-full object-cover"
          />
        </picture>

        {/* Dark Overlay for Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight drop-shadow-2xl" data-testid="hero-title" style={{ textShadow: '2px 2px 8px rgba(0, 0, 0, 0.9)' }}>
              Expert Environmental Consulting for Alaska
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-gray-50 leading-relaxed drop-shadow-lg" data-testid="hero-subtitle" style={{ textShadow: '1px 1px 6px rgba(0, 0, 0, 0.8)' }}>
              Helping Alaska's industries navigate environmental regulations with confidence — from air quality permits to compliance solutions.
            </p>
            <p className="text-lg md:text-xl mb-8 text-emerald-200 font-medium drop-shadow-lg" data-testid="hero-credibility" style={{ textShadow: '1px 1px 6px rgba(0, 0, 0, 0.8)' }}>
              Over 13 years supporting Alaska's power, oil & gas, mining, and seafood industries.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105"
              >
                Request Consultation
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-learn"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/20 backdrop-blur-sm border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-emerald-700 transition-all shadow-lg"
              >
                View Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-gray-50" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Alaska-Owned Environmental Excellence
              </h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Raven Environmental Consulting, LLC is an Alaskan-owned small business offering comprehensive air quality support services to diverse industries including power generation, oil and gas extraction, military installations, seafood processing, soil remediation, and more.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Our expertise in{' '}
                <Link to="/permit-feasibility" className="text-emerald-700 font-semibold hover:text-emerald-800 hover:underline transition-colors">
                  environmental permits
                </Link>
                ,{' '}
                <Link to="/services/compliance-support" className="text-emerald-700 font-semibold hover:text-emerald-800 hover:underline transition-colors">
                  air quality compliance
                </Link>
                ,{' '}
                <Link to="/services/regulatory-guidance" className="text-emerald-700 font-semibold hover:text-emerald-800 hover:underline transition-colors">
                  regulatory support
                </Link>
                , and{' '}
                <Link to="/services/permit-applications" className="text-emerald-700 font-semibold hover:text-emerald-800 hover:underline transition-colors">
                  permit applications
                </Link>
                {' '}ensures compliance with environmental regulations while promoting sustainable practices across Alaska's unique industrial landscape.
              </p>
              <Link
                to="/industries"
                data-testid="about-cta"
                className="inline-flex items-center text-emerald-700 font-semibold hover:text-emerald-800"
              >
                Explore Industries We Serve
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1759182391264-73c1be282907?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxhbGFza2ElMjBlbnZpcm9ubWVudGFsJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc2MTEwMTE4NHww&ixlib=rb-4.1.0&q=85"
                alt="Alaska environmental landscape with river and mountains"
                className="rounded-lg shadow-xl"
                data-testid="about-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Raven Section - Responsive Accordion/Grid */}
      <WhyChooseRaven />

      {/* Industries Section - Image Grid */}
      <section className="py-20 bg-gray-50" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-600">
              Providing specialized environmental consulting across diverse Alaska industries.
            </p>
          </div>

          <div className="max-w-7xl mx-auto">
            <div className="industry-cards-grid">
              {/* Oil & Gas */}
              <Link
                to="/sectors/oil-and-gas"
                data-testid="industry-oil-gas"
                className="group flex flex-col h-full bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="relative w-full h-48 overflow-hidden flex items-center justify-center">
                  <picture className="w-full h-full flex items-center justify-center">
                    <source 
                      srcSet="/images/onshore-oil-gas-1600w.webp" 
                      media="(min-width: 1440px)" 
                    />
                    <source 
                      srcSet="/images/onshore-oil-gas-1024w.webp" 
                      media="(min-width: 1024px)" 
                    />
                    <source 
                      srcSet="/images/onshore-oil-gas-640w.webp" 
                      media="(min-width: 768px)" 
                    />
                    <img 
                      src="/images/onshore-oil-gas-320w.webp" 
                      alt="Onshore Oil & Gas Facility" 
                      loading="lazy"
                      decoding="async"
                      className="industry-card-img group-hover:scale-105 transition-transform duration-300"
                      style={{ 
                        width: '100%', 
                        height: 'auto', 
                        objectFit: 'contain', 
                        background: 'transparent',
                        aspectRatio: '1 / 1',
                        maxHeight: '100%'
                      }}
                    />
                  </picture>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors min-h-[64px]">
                    Oil & Gas
                  </h4>
                  <p className="text-gray-600 text-sm flex-1">
                    Comprehensive permitting and compliance for Alaska's energy sector
                  </p>
                  <span className="text-emerald-700 font-semibold text-sm group-hover:text-emerald-800 inline-flex items-center">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </span>
                </div>
              </Link>

              {/* Mining & Road Construction */}
              <Link
                to="/sectors/mining-construction"
                data-testid="industry-mining"
                className="group flex flex-col h-full bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="relative w-full h-48 overflow-hidden flex items-center justify-center">
                  <picture className="w-full h-full flex items-center justify-center">
                    <source 
                      srcSet="/images/mining-road-construction-1600w.webp" 
                      media="(min-width: 1200px)" 
                    />
                    <source 
                      srcSet="/images/mining-road-construction-1024w.webp" 
                      media="(min-width: 768px)" 
                    />
                    <source 
                      srcSet="/images/mining-road-construction-640w.webp" 
                      media="(min-width: 480px)" 
                    />
                    <img 
                      src="/images/mining-road-construction-320w.webp" 
                      alt="Mining and Road Construction heavy equipment" 
                      loading="lazy"
                      decoding="async"
                      className="industry-card-img group-hover:scale-105 transition-transform duration-300"
                      style={{ 
                        width: '100%', 
                        height: 'auto', 
                        objectFit: 'contain', 
                        background: 'transparent',
                        aspectRatio: '1 / 1',
                        maxHeight: '100%'
                      }}
                    />
                  </picture>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors min-h-[64px]">
                    Mining & Road Construction
                  </h4>
                  <p className="text-gray-600 text-sm flex-1">
                    Environmental solutions for extraction and construction projects
                  </p>
                  <span className="text-emerald-700 font-semibold text-sm group-hover:text-emerald-800 inline-flex items-center">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </span>
                </div>
              </Link>

              {/* Power Plants */}
              <Link
                to="/sectors/power-plants"
                data-testid="industry-power-plants"
                className="group flex flex-col h-full bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="relative w-full h-48 overflow-hidden flex items-center justify-center">
                  <picture className="w-full h-full flex items-center justify-center">
                    <source 
                      srcSet="/images/power-plant-1280w.webp" 
                      media="(min-width: 1200px)" 
                    />
                    <source 
                      srcSet="/images/power-plant-960w.webp" 
                      media="(min-width: 992px)" 
                    />
                    <source 
                      srcSet="/images/power-plant-640w.webp" 
                      media="(min-width: 768px)" 
                    />
                    <img 
                      src="/images/power-plant-320w.webp" 
                      alt="Power Plant" 
                      loading="lazy"
                      decoding="async"
                      className="industry-card-img group-hover:scale-105 transition-transform duration-300"
                      style={{ 
                        width: '100%', 
                        height: 'auto', 
                        objectFit: 'contain', 
                        background: 'transparent',
                        aspectRatio: '4 / 3',
                        maxHeight: '100%',
                        display: 'block',
                        margin: '0 auto'
                      }}
                    />
                  </picture>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors min-h-[64px]">
                    Power Plants
                  </h4>
                  <p className="text-gray-600 text-sm flex-1">
                    Regulatory expertise for Alaska's power facilities and utilities
                  </p>
                  <span className="text-emerald-700 font-semibold text-sm group-hover:text-emerald-800 inline-flex items-center">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </span>
                </div>
              </Link>

              {/* Coal Fired Power Plants */}
              <Link
                to="/sectors/coal-fired-power-plants"
                data-testid="industry-coal-fired"
                className="group flex flex-col h-full bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="relative w-full h-48 overflow-hidden flex items-center justify-center">
                  <picture className="w-full h-full flex items-center justify-center">
                    <source 
                      srcSet="https://customer-assets.emergentagent.com/job_raven-solutions/artifacts/it78o3pq_coal-fired-power-plant-1600w.webp" 
                      media="(min-width:1200px)" 
                      type="image/webp"
                    />
                    <source 
                      srcSet="https://customer-assets.emergentagent.com/job_raven-solutions/artifacts/0zpsmu8q_coal-fired-power-plant-1024w.webp" 
                      media="(min-width:768px)" 
                      type="image/webp"
                    />
                    <source 
                      srcSet="https://customer-assets.emergentagent.com/job_raven-solutions/artifacts/00fjeupp_coal-fired-power-plant-640w.webp" 
                      media="(min-width:480px)" 
                      type="image/webp"
                    />
                    <img
                      src="https://customer-assets.emergentagent.com/job_raven-solutions/artifacts/mmk1qvpe_coal-fired-power-plant-320w.webp"
                      alt="Coal-Fired Power Plant Environmental Consulting"
                      loading="lazy"
                      className="group-hover:scale-105 transition-transform duration-300"
                      style={{ 
                        width: '100%', 
                        height: 'auto', 
                        objectFit: 'contain', 
                        background: 'transparent',
                        maxHeight: '100%',
                        display: 'block',
                        margin: '0 auto'
                      }}
                    />
                  </picture>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors min-h-[64px]">
                    Coal Fired Power Plants
                  </h4>
                  <p className="text-gray-600 text-sm flex-1">
                    Specialized compliance strategies for coal-fired facilities
                  </p>
                  <span className="text-emerald-700 font-semibold text-sm group-hover:text-emerald-800 inline-flex items-center">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </span>
                </div>
              </Link>

              {/* Military Installations */}
              <Link
                to="/sectors/military-installations"
                data-testid="industry-military"
                className="group flex flex-col h-full bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="relative w-full h-48 overflow-hidden flex items-center justify-center">
                  <picture>
                    <source 
                      srcSet="/images/military_installation_320.webp" 
                      media="(max-width: 640px)" 
                      type="image/webp"
                    />
                    <source 
                      srcSet="/images/military_installation_640.webp" 
                      media="(min-width: 641px) and (max-width: 1024px)" 
                      type="image/webp"
                    />
                    <source 
                      srcSet="/images/military_installation_960.webp" 
                      media="(min-width: 1025px) and (max-width: 1440px)" 
                      type="image/webp"
                    />
                    <source 
                      srcSet="/images/military_installation_1280.webp" 
                      media="(min-width: 1441px)" 
                      type="image/webp"
                    />
                    <img 
                      src="/images/military_installation_960.webp"
                      alt="Isometric illustration of a military installation representing environmental permitting, compliance, and operational support provided by Raven Environmental Consulting across Alaska."
                      loading="lazy"
                      className="group-hover:scale-105 transition-transform duration-300"
                      style={{ 
                        width: '100%', 
                        height: 'auto', 
                        objectFit: 'contain', 
                        background: 'transparent',
                        maxHeight: '100%',
                        display: 'block',
                        margin: '0 auto'
                      }}
                    />
                  </picture>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors min-h-[64px]">
                    Military Installations
                  </h4>
                  <p className="text-gray-600 text-sm flex-1">
                    Environmental support for defense installations and operations
                  </p>
                  <span className="text-emerald-700 font-semibold text-sm group-hover:text-emerald-800 inline-flex items-center">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </span>
                </div>
              </Link>

              {/* Seafood Processing */}
              <Link
                to="/sectors/seafood-processing"
                data-testid="industry-seafood"
                className="group flex flex-col h-full bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="relative w-full h-48 overflow-hidden flex items-center justify-center">
                  <picture className="w-full h-full flex items-center justify-center">
                    <source 
                      srcSet="/images/seafood-processing-320w.webp" 
                      media="(max-width: 480px)" 
                      type="image/webp"
                    />
                    <source 
                      srcSet="/images/seafood-processing-640w.webp" 
                      media="(max-width: 768px)" 
                      type="image/webp"
                    />
                    <source 
                      srcSet="/images/seafood-processing-960w.webp" 
                      media="(max-width: 1200px)" 
                      type="image/webp"
                    />
                    <source 
                      srcSet="/images/seafood-processing-1280w.webp" 
                      media="(min-width: 1201px)" 
                      type="image/webp"
                    />
                    <img 
                      src="/images/seafood-processing-640w.webp" 
                      alt="Seafood Processing Facility Illustration" 
                      loading="lazy"
                      decoding="async"
                      className="group-hover:scale-105 transition-transform duration-300"
                      style={{ 
                        width: '100%', 
                        height: 'auto', 
                        objectFit: 'contain', 
                        background: 'transparent',
                        maxHeight: '100%',
                        display: 'block',
                        margin: '0 auto'
                      }}
                    />
                  </picture>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors min-h-[64px]">
                    Seafood Processing
                  </h4>
                  <p className="text-gray-600 text-sm flex-1">
                    Compliance solutions for Alaska's seafood industry
                  </p>
                  <span className="text-emerald-700 font-semibold text-sm group-hover:text-emerald-800 inline-flex items-center">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </span>
                </div>
              </Link>

              {/* Soil Remediation */}
              <Link
                to="/sectors/soil-remediation"
                data-testid="industry-soil"
                className="group flex flex-col h-full bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="relative w-full h-48 overflow-hidden flex items-center justify-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_raven-solutions/artifacts/3ju0s2k3_Soil%20Remediation.png"
                    alt="Soil Remediation"
                    loading="lazy"
                    className="group-hover:scale-105 transition-transform duration-300"
                    style={{ 
                      width: '100%', 
                      height: 'auto', 
                      objectFit: 'contain', 
                      background: 'transparent',
                      maxHeight: '100%',
                      display: 'block',
                      margin: '0 auto'
                    }}
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors min-h-[64px]">
                    Soil Remediation
                  </h4>
                  <p className="text-gray-600 text-sm mb-4 flex-1">
                    Environmental cleanup and restoration services
                  </p>
                  <span className="text-emerald-700 font-semibold text-sm group-hover:text-emerald-800 inline-flex items-center">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </span>
                </div>
              </Link>

              {/* Geothermal */}
              <Link
                to="/sectors/geothermal-permitting"
                data-testid="industry-geothermal"
                className="group flex flex-col h-full bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="relative w-full h-48 overflow-hidden flex items-center justify-center">
                  <picture className="w-full h-full flex items-center justify-center">
                    <source 
                      srcSet="/images/geothermal-powerplant-1280w-optimized.png" 
                      media="(min-width: 1200px)"
                    />
                    <source 
                      srcSet="/images/geothermal-powerplant-960w-optimized.png" 
                      media="(min-width: 768px)"
                    />
                    <source 
                      srcSet="/images/geothermal-powerplant-640w-optimized.png" 
                      media="(min-width: 480px)"
                    />
                    <img 
                      src="/images/geothermal-powerplant-320w-optimized.png" 
                      alt="Geothermal Power Plant" 
                      loading="lazy" 
                      decoding="async"
                      className="group-hover:scale-105 transition-transform duration-300"
                      style={{ 
                        width: '100%', 
                        height: 'auto', 
                        objectFit: 'contain', 
                        background: 'transparent',
                        maxHeight: '100%',
                        display: 'block',
                        margin: '0 auto'
                      }}
                    />
                  </picture>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors min-h-[64px]">
                    Geothermal
                  </h4>
                  <p className="text-gray-600 text-sm mb-4 flex-1">
                    Permitting expertise for renewable geothermal energy projects
                  </p>
                  <span className="text-emerald-700 font-semibold text-sm group-hover:text-emerald-800 inline-flex items-center">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </span>
                </div>
              </Link>

              {/* Antimony Roasting */}
              <Link
                to="/sectors/antimony-roasting"
                data-testid="industry-antimony"
                className="group flex flex-col h-full bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="relative w-full h-48 overflow-hidden flex items-center justify-center">
                  <picture className="w-full h-full flex items-center justify-center">
                    <source 
                      srcSet="/images/antimony-roaster-1280w.webp" 
                      media="(min-width: 1200px)" 
                    />
                    <source 
                      srcSet="/images/antimony-roaster-960w.webp" 
                      media="(min-width: 768px)" 
                    />
                    <source 
                      srcSet="/images/antimony-roaster-640w.webp" 
                      media="(min-width: 480px)" 
                    />
                    <img 
                      src="/images/antimony-roaster-320w.webp" 
                      alt="Antimony Roasting Facility" 
                      loading="lazy"
                      decoding="async"
                      className="group-hover:scale-105 transition-transform duration-300"
                      style={{ 
                        width: '100%', 
                        height: 'auto', 
                        objectFit: 'contain', 
                        background: 'transparent',
                        maxHeight: '100%',
                        display: 'block',
                        margin: '0 auto'
                      }}
                    />
                  </picture>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors min-h-[64px]">
                    Antimony Roasting
                  </h4>
                  <p className="text-gray-600 text-sm mb-4 flex-1">
                    Specialized permitting for high-temperature metal roasting operations
                  </p>
                  <span className="text-emerald-700 font-semibold text-sm group-hover:text-emerald-800 inline-flex items-center">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;